import Vue from 'vue'
import Router from 'vue-router'
import lazy from '@lib/router/lazy'
import store from './state/store.js'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  routes: [{
    path: '/dashboard',
    name: 'dashboard',
    component: () => lazy(import('./views/dashboard')),
    meta: {
      menu: 'dashboard',
    },
  }, {
    path: '/providers',
    name: 'providers',
    component: () => lazy(import('./views/providers')),
    meta: {
      menu: 'providers',
      permissions: ['provider:read'],
    },
    beforeEnter(to, from, next) {
      const canSearchProvider = store.getters['me/canSearchProvider']
      if (!canSearchProvider) {
        next({ name: 'dashboard' })
      } else {
        next()
      }
    },
  }, {
    path: '/providers/new',
    name: 'provider-create',
    component: () => lazy(import('./views/providers/provider-manage')),
    meta: {
      menu: 'providers',
      permissions: ['provider:create'],
    },
    beforeEnter(to, from, next) {
      const canSearchProvider = store.getters['me/canSearchProvider']
      if (!canSearchProvider) {
        next({ name: 'dashboard' })
      } else {
        next()
      }
    },
  }, {
    path: '/providers/:id',
    name: 'provider-update',
    component: () => lazy(import('./views/providers/provider-manage')),
    meta: {
      menu: 'providers',
      permissions: ['provider:read', 'provider:update'],
    },
    beforeEnter(to, from, next) {
      const canSearchProvider = store.getters['me/canSearchProvider']
      if (!canSearchProvider) {
        next({ name: 'dashboard' })
      } else {
        next()
      }
    },
  }, {
    path: '/provider',
    name: 'provider',
    component: () => lazy(import('./views/providers/provider-manage')),
    meta: {
      menu: 'provider',
      permissions: ['provider:read'],
    },
    beforeEnter(to, from, next) {
      const providerId = store.getters['me/providerId']
      if (!providerId) {
        next({ name: 'dashboard' })
      } else {
        next()
      }
    },
  }, {
    path: '/api-keys',
    name: 'api-keys',
    component: () => lazy(import('./views/api-keys')),
    meta: {
      menu: 'api-keys',
      permissions: ['api_key:read'],
    },
  }, {
    path: '/api-key/create',
    name: 'api-key-create',
    component: () => lazy(import('./views/api-key/api-key-form')),
    props: true,
    meta: {
      menu: 'api-keys',
      permissions: ['api_key:create'],
    },
  }, {
    path: '/api-key/:id',
    name: 'api-key',
    component: () => lazy(import('./views/api-key/api-key-details')),
    props: true,
    meta: {
      menu: 'api-keys',
      permissions: ['api_key:read'],
    },
  }, {
    path: '/api-key/:id/edit',
    name: 'api-key-edit',
    component: () => import('./views/api-key/api-key-form.vue'),
    props: true,
    meta: {
      skipFromBackButton: true,
      menu: 'api-keys',
      edit: true,
      scopes: ['api_key:update'],
    },
  }, {
    path: '/retailers',
    name: 'retailers',
    component: () => lazy(import('./views/retailers')),
    meta: {
      menu: 'retailers',
      permissions: ['retailer:read'],
    },
  }, {
    path: '/retailers/new',
    name: 'retailer-new',
    component: () => lazy(import('./views/retailer-new')),
    meta: {
      skipFromBackButton: true,
      menu: 'retailers',
      permissions: ['retailer:create'],
    },
  }, {
    path: '/retailers/:id/offers',
    name: 'retailer-offers',
    component: () => lazy(import('./views/retailer/offers/retailer-offers')),
    meta: {
      menu: 'retailers',
      permissions: [['offer:create', 'offer:update']],
    },
  }, {
    path: '/retailers/:id/offers/preview',
    name: 'retailer-offers-preview',
    component: () => lazy(import('./views/retailer/offers/retailer-offers-preview')),
    props: true,
    meta: {
      menu: 'retailers',
      permissions: ['offer:read'],
    },
  }, {
    path: '/retailers/:id',
    name: 'retailer-details',
    component: () => lazy(import('./views/retailer')),
    props: true,
    meta: {
      menu: 'retailers',
      permissions: ['retailer:read'],
    },
  }, {
    path: '/retailers/:id/edit',
    name: 'retailer-edit',
    component: () => lazy(import('./views/retailer')),
    props: true,
    meta: {
      skipFromBackButton: true,
      menu: 'retailers',
      permissions: ['retailer:update'],
    },
  }, {
    path: '/offers',
    name: 'offers',
    component: () => lazy(import('./views/offers')),
    meta: {
      menu: 'offers',
      permissions: ['offer:read'],
    },
  }, {
    path: '/offers/:id',
    name: 'offer-details',
    component: () => lazy(import('./views/offer')),
    props: true,
    meta: {
      menu: 'offers',
      permissions: ['offer:read'],
    },
  }, {
    path: '/categories',
    name: 'categories',
    component: () => lazy(import('./views/categories')),
    meta: {
      menu: 'categories',
      permissions: ['category:read'],
    },
  }, {
    path: '/categories/new',
    name: 'category-new',
    component: () => lazy(import('./views/categories/category-new')),
    meta: {
      skipFromBackButton: true,
      menu: 'categories',
      permissions: ['category:create'],
    },
  }, {
    path: '/categories/:id/:edit',
    name: 'category-details',
    component: () => lazy(import('~/views/categories/category-details')),
    props: true,
    meta: {
      skipFromBackButton: true,
      menu: 'categories',
      permissions: ['category:update'],
    },
  }, {
    path: '/log',
    name: 'log',
    component: () => lazy(import('./views/log')),
    meta: {
      menu: 'log',
    },
  },
  {
    path: '/offer-groups',
    name: 'offer-groups',
    component: () => lazy(import('./views/offer-groups')),
    meta: {
      menu: 'offer-groups',
      permissions: ['offer:read'],
    },
  },
  {
    path: '/offer-groups/new',
    name: 'offer-group-create',
    component: () => lazy(import('./views/offer-group/offer-group-manage')),
    meta: {
      menu: 'offer-groups',
      permissions: ['offer:create'],
    },
  },
  {
    path: '/offer-groups/:id',
    name: 'offer-group-details',
    component: () => lazy(import('./views/offer-group')),
    props: true,
    meta: {
      menu: 'offer-groups',
      permissions: ['offer:read'],
    },
  },
  {
    path: '/offer-groups/update/:id',
    name: 'offer-group-update',
    component: () => lazy(import('./views/offer-group/offer-group-manage')),
    meta: {
      menu: 'offer-groups',
      permissions: ['offer:create'],
    },
  },
  {
    path: '/',
    name: '*',
    redirect: '/dashboard',
  }],
})

// auth0 scope access check
router.beforeEach(async (to, from, next) => {
  if (!store.state.auth.accessToken) {
    return next()
  }

  const { permissions } = to.meta
  // await until scopes are loaded
  await store.state.me.loadedPromise

  if (permissions) {
    // check if current user have all required scopes
    if (!permissions.every(permission => Array.isArray(permission)
      ? permission.some(store.getters['me/hasPermission'])
      : store.getters['me/hasPermission']
    )) {
      return next({ name: '404' })
    }
  }

  next()
})

export default router
